
import React, { useState } from 'react';
import { Contact, Deal, Activity, ResearchResult, Task, Employee, User as UserType } from '../types';
import { generateEmailDraft, researchCompany, generateWhatsAppDraft } from '../services/gemini';
import AddContactModal from './AddContactModal';
import { Building2, MapPin, Mail, Phone, Hash, ShieldCheck, Fingerprint, X, Plus, Loader2, User, Trash2 } from 'lucide-react';

interface ContactDetailProps {
  contact: Contact;
  currentUser: UserType;
  deals: Deal[];
  activities: Activity[];
  tasks: Task[];
  employees: Employee[];
  onBack: () => void;
  onUpdateContact: (contact: Contact) => void;
  onDeleteContact?: (id: string) => void;
  onAddActivity: (activity: Activity) => void;
  onAddTasks?: (task: Task) => void; // Fixed name mismatch if any
  onAddTask: (task: Task) => void;
  onToggleTask: (taskId: string) => void;
}

const ContactDetail: React.FC<ContactDetailProps> = ({ 
  contact, 
  currentUser,
  deals, 
  activities, 
  tasks,
  employees,
  onBack, 
  onUpdateContact,
  onDeleteContact,
  onAddActivity,
  onAddTask,
  onToggleTask
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'email' | 'whatsapp' | 'tasks' | 'research'>('overview');
  const [emailContext, setEmailContext] = useState('');
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  
  const [whatsappContext, setWhatsappContext] = useState('');
  const [generatedWhatsapp, setGeneratedWhatsapp] = useState('');
  const [isGeneratingWhatsapp, setIsGeneratingWhatsapp] = useState(false);

  const [researchData, setResearchData] = useState<ResearchResult | null>(null);
  const [isResearching, setIsResearching] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [activityType, setActivityType] = useState<'Email' | 'Call' | 'Meeting' | 'Note' | 'WhatsApp'>('Call');
  const [activityDesc, setActivityDesc] = useState('');
  const [activityDate, setActivityDate] = useState(new Date().toISOString().split('T')[0]);

  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDate, setNewTaskDate] = useState(new Date().toISOString().split('T')[0]);
  const [newTaskPriority, setNewTaskPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [newTaskAssignee, setNewTaskAssignee] = useState<string>('');

  const isAdmin = currentUser.role === 'Admin';
  const canAddEdit = isAdmin || currentUser.role === 'Add/Edit';
  const isReadOnly = currentUser.role === 'Read Only';

  const handleGenerateEmail = async () => {
    if (!emailContext || isReadOnly) return;
    setIsGeneratingEmail(true);
    try {
      const draft = await generateEmailDraft(contact, emailContext);
      setGeneratedEmail(draft || '');
    } catch (error) {
      console.error("Failed to generate email", error);
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  const handleGenerateWhatsapp = async () => {
    if (!whatsappContext || isReadOnly) return;
    setIsGeneratingWhatsapp(true);
    try {
      const draft = await generateWhatsAppDraft(contact, whatsappContext);
      setGeneratedWhatsapp(draft || '');
    } catch (error) {
      console.error("Failed to generate whatsapp message", error);
    } finally {
      setIsGeneratingWhatsapp(false);
    }
  };

  const handleSendWhatsapp = () => {
    if (isReadOnly) return;
    const cleanPhone = contact.phone.replace(/[^0-9]/g, '');
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(generatedWhatsapp)}`;
    window.open(url, '_blank');

    const newActivity: Activity = {
      id: Date.now().toString(),
      contactId: contact.id,
      type: 'WhatsApp',
      date: new Date().toISOString().split('T')[0],
      description: `Sent message: "${generatedWhatsapp.substring(0, 50)}${generatedWhatsapp.length > 50 ? '...' : ''}"`
    };
    onAddActivity(newActivity);
  };

  const handleResearch = async () => {
    setIsResearching(true);
    try {
      const result = await researchCompany(contact.companyName);
      setResearchData(result);
    } catch (error) {
      console.error("Failed to research company", error);
    } finally {
      setIsResearching(false);
    }
  };

  const handleSubmitActivity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activityDesc || isReadOnly) return;

    const newActivity: Activity = {
      id: Date.now().toString(),
      contactId: contact.id,
      type: activityType,
      date: activityDate,
      description: activityDesc
    };

    onAddActivity(newActivity);
    setActivityDesc('');
    setActivityDate(new Date().toISOString().split('T')[0]);
  };

  const handleAddTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle || !canAddEdit) return;

    const newTask: Task = {
      id: Date.now().toString(),
      contactId: contact.id,
      title: newTaskTitle,
      dueDate: newTaskDate,
      priority: newTaskPriority,
      status: 'Pending',
      assigneeId: newTaskAssignee || undefined,
      country: contact.country
    };

    onAddTask(newTask);
    setNewTaskTitle('');
    setNewTaskDate(new Date().toISOString().split('T')[0]);
    setNewTaskPriority('Medium');
    setNewTaskAssignee('');
  };

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Dynamic Header */}
      <div className="px-8 py-8 border-b border-slate-100 flex items-center gap-6 bg-slate-50/50">
        <button 
          onClick={onBack}
          className="p-3 hover:bg-white rounded-2xl transition-all text-slate-400 border border-transparent hover:border-slate-200 hover:shadow-sm"
        >
          <X size={20} />
        </button>
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center border border-slate-200 shadow-xl overflow-hidden">
            <img src={contact.avatarUrl} alt="" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="flex items-center gap-3">
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight leading-none">
                {contact.companyName}
                </h2>
                <div className="flex items-center gap-1">
                  {canAddEdit && (
                    <button 
                      onClick={() => setIsEditModalOpen(true)}
                      className="p-2 text-slate-300 hover:text-indigo-600 transition-colors"
                      title="Edit Customer Profile"
                    >
                      <Fingerprint size={18} />
                    </button>
                  )}
                  {isAdmin && (
                    <button 
                      onClick={() => onDeleteContact?.(contact.id)}
                      className="p-2 text-slate-300 hover:text-rose-600 transition-colors"
                      title="Purge Customer Record"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
            </div>
            <p className="text-sm text-slate-500 font-bold uppercase tracking-widest mt-2 flex items-center gap-2">
                <ShieldCheck size={14} className="text-emerald-500" />
                Verified Entity • {contact.country} Division
            </p>
          </div>
        </div>
        <div className="ml-auto flex gap-4">
           <div className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border shadow-sm
             ${contact.sentiment === 'Positive' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-100 text-slate-700 border-slate-200'}
           `}>
             Sentiment: {contact.sentiment}
           </div>
        </div>
      </div>

      <div className="px-8 border-b border-slate-100 bg-white">
        <div className="flex gap-8">
          {['overview', 'email', 'whatsapp', 'tasks', 'research'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-5 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${
                activeTab === tab 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              {tab === 'whatsapp' ? 'Direct WhatsApp' : tab}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-hidden grid grid-cols-12 bg-slate-50/30">
        <div className="col-span-8 overflow-y-auto p-8 custom-scrollbar">
            {activeTab === 'overview' && (
                <div className="space-y-8 animate-in fade-in duration-500">
                    {!isReadOnly && (
                      <div className="bg-white p-8 rounded-[40px] shadow-sm border border-slate-200">
                          <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                              <Plus size={14} className="text-indigo-500" />
                              Log Interaction
                          </h3>
                          <form onSubmit={handleSubmitActivity} className="space-y-6">
                              <div className="flex flex-col sm:flex-row gap-4">
                                  <select 
                                      value={activityType}
                                      onChange={(e) => setActivityType(e.target.value as any)}
                                      className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                  >
                                      <option value="Call">Phone Call</option>
                                      <option value="Email">Email Thread</option>
                                      <option value="WhatsApp">WhatsApp</option>
                                      <option value="Meeting">Direct Meeting</option>
                                      <option value="Note">Internal Note</option>
                                  </select>
                                  <input 
                                      type="text" 
                                      placeholder="Briefly describe the outcome..." 
                                      value={activityDesc}
                                      onChange={(e) => setActivityDesc(e.target.value)}
                                      className="flex-1 px-6 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                  />
                                  <button 
                                      type="submit" 
                                      disabled={!activityDesc}
                                      className="px-8 py-3 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 disabled:opacity-30 transition-all shadow-xl shadow-slate-100"
                                  >
                                      Append Logs
                                  </button>
                              </div>
                          </form>
                      </div>
                    )}

                    <div className="bg-white p-8 rounded-[40px] shadow-sm border border-slate-200">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-8">Activity Ledger</h3>
                        <div className="space-y-6">
                        {activities.length > 0 ? activities.map((activity) => (
                            <div key={activity.id} className="flex gap-6 animate-in fade-in slide-in-from-top-2 duration-300 relative group">
                            <div className={`mt-1 w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm shrink-0 transition-transform group-hover:scale-110
                                ${activity.type === 'Call' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 
                                activity.type === 'Email' ? 'bg-indigo-50 text-indigo-600 border border-indigo-100' :
                                activity.type === 'WhatsApp' ? 'bg-emerald-100 text-emerald-700' :
                                activity.type === 'Meeting' ? 'bg-purple-50 text-purple-600 border border-purple-100' :
                                'bg-slate-50 text-slate-600 border border-slate-100'
                                }`}>
                                <Fingerprint size={18} />
                            </div>
                            <div className="flex-1 pb-6 border-b border-slate-50">
                                <div className="flex items-center justify-between mb-1">
                                <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest">{activity.type}</p>
                                <span className="text-[10px] font-bold text-slate-300 uppercase tracking-tighter">{activity.date}</span>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed font-medium">{activity.description}</p>
                            </div>
                            </div>
                        )) : (
                            <div className="py-12 text-center text-slate-300 italic text-[10px] font-black uppercase tracking-widest">Registry Zero Interaction State</div>
                        )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'tasks' && (
                <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-500">
                   {canAddEdit && (
                     <div className="bg-white p-8 rounded-[40px] shadow-sm border border-slate-200">
                          <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-6">Assign Deliverable</h3>
                          <form onSubmit={handleAddTask} className="space-y-4">
                              <input 
                                  type="text" 
                                  required
                                  value={newTaskTitle}
                                  onChange={(e) => setNewTaskTitle(e.target.value)}
                                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                                  placeholder="Task description..."
                              />
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <input 
                                      type="date" 
                                      value={newTaskDate}
                                      onChange={(e) => setNewTaskDate(e.target.value)}
                                      className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[10px] font-black uppercase outline-none"
                                  />
                                  <select 
                                      value={newTaskPriority}
                                      onChange={(e) => setNewTaskPriority(e.target.value as any)}
                                      className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[10px] font-black uppercase outline-none"
                                  >
                                      <option value="High">Priority: High</option>
                                      <option value="Medium">Priority: Medium</option>
                                      <option value="Low">Priority: Low</option>
                                  </select>
                                  <button 
                                      type="submit" 
                                      className="bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all"
                                  >
                                      Finalize Task
                                  </button>
                              </div>
                          </form>
                     </div>
                   )}
                   {/* Task list view already handle toggling logic */}
                </div>
            )}

            {activeTab === 'email' && (
                <div className="max-w-3xl mx-auto animate-in slide-in-from-bottom-4 duration-500">
                    <div className="bg-white p-8 rounded-[40px] shadow-sm border border-slate-200">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center border border-purple-100 shadow-sm"><Mail size={18}/></div>
                            <h3 className="font-black text-slate-800 uppercase tracking-tight">AI Email Composer</h3>
                        </div>
                        <div className="space-y-6">
                            <textarea
                                readOnly={isReadOnly}
                                value={emailContext}
                                onChange={(e) => setEmailContext(e.target.value)}
                                placeholder={isReadOnly ? "Audit Mode: AI Generation Disabled" : "Describe the goal: e.g., Follow up on the bitumen supply for Morogoro..."}
                                className="w-full h-32 px-6 py-4 rounded-3xl bg-slate-50 border border-slate-200 focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none resize-none transition-all text-sm leading-relaxed"
                            />
                            {!isReadOnly && (
                              <button
                                  onClick={handleGenerateEmail}
                                  disabled={isGeneratingEmail || !emailContext}
                                  className="w-full bg-slate-900 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-slate-800 disabled:opacity-30 transition-all shadow-xl flex items-center justify-center gap-3"
                              >
                                  {isGeneratingEmail ? <Loader2 className="animate-spin" size={18} /> : '✨ Compose with Nexus AI'}
                              </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>

        <div className="col-span-4 bg-white border-l border-slate-100 overflow-y-auto p-8 custom-scrollbar">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-8 border-b border-slate-50 pb-4">Corporate Master Data</h3>
            
            <div className="space-y-8">
                <section className="space-y-4">
                    <div className="flex items-center gap-3 text-indigo-600 mb-2">
                        <Building2 size={18} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Business Identity</span>
                    </div>
                    <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-1">Company Name</p>
                            <p className="text-sm font-black text-slate-900 uppercase tracking-tight">{contact.companyName}</p>
                        </div>
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-1">Address</p>
                            <p className="text-xs font-bold text-slate-600 leading-relaxed flex items-start gap-2">
                                <MapPin size={12} className="shrink-0 mt-0.5 text-slate-300" />
                                {contact.companyAddress}
                            </p>
                        </div>
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-1">P.O. Box</p>
                            <p className="text-xs font-bold text-slate-600">{contact.poBox || 'Registry Unset'}</p>
                        </div>
                    </div>
                </section>

                <section className="space-y-4">
                    <div className="flex items-center gap-3 text-emerald-600 mb-2">
                        <Hash size={18} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Taxation Profile</span>
                    </div>
                    <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">TIN (Tax ID)</p>
                                <p className="text-sm font-mono font-black text-slate-800">{contact.tin || 'N/A'}</p>
                            </div>
                            <ShieldCheck className="text-emerald-500" size={20} />
                        </div>
                        <div className="border-t border-slate-100 pt-4">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">VRN (Value Added Tax)</p>
                            <p className="text-sm font-mono font-black text-slate-800">{contact.vrn || 'EXEMPT'}</p>
                        </div>
                    </div>
                </section>

                <section className="space-y-4">
                    <div className="flex items-center gap-3 text-indigo-400 mb-2">
                        <User size={18} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Lead Individual</span>
                    </div>
                    <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-1">Primary Representative</p>
                            <p className="text-sm font-black text-slate-900">{contact.contactPerson}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{contact.role}</p>
                        </div>
                        <div className="space-y-2 border-t border-slate-100 pt-4">
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-3 text-xs font-bold text-slate-600 hover:text-indigo-600 transition-colors">
                                <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center shadow-sm"><Phone size={12}/></div>
                                {contact.phone}
                            </a>
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-3 text-xs font-bold text-slate-600 hover:text-indigo-600 transition-colors">
                                <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center shadow-sm"><Mail size={12}/></div>
                                {contact.email}
                            </a>
                        </div>
                    </div>
                </section>
            </div>
        </div>
      </div>

      <AddContactModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={(updatedContact) => {
          onUpdateContact(updatedContact);
          setIsEditModalOpen(false);
        }}
        initialData={contact}
      />
    </div>
  );
};

export default ContactDetail;
